import classNames from 'classnames';
import { Badge, Button, CloseButton } from 'react-bootstrap';
/**
 * Provides the standardized visual layout and content structure for map popups.
 *
 * `BaseMapPopup` handles the presentation of popup content, including the
 * header, title, subtitle, icon, status, metadata, scrollable body, footer,
 * responsive width, and compact display options.
 *
 * This component does not create or manage a Leaflet popup itself. It must
 * still be rendered inside a Leaflet `Popup` component.
 *
 * @example
 * <Popup closeButton={false}>
 *   <BaseMapPopup
 *     title="Weather Station"
 *     status="Online"
 *     onClose={() => map.closePopup()}
 *   >
 *     <WeatherStationDetails />
 *   </BaseMapPopup>
 * </Popup>
 */
const BaseMapPopup = ({
  title,
  subtitle,
  eyebrow,
  meta,
  status,
  statusVariant = 'success',
  statusClassName,
  icon,
  children,
  footer,
  onClose,
  className,
  bodyClassName,
  footerClassName,
  size = 'md',
  density = 'default',
}) => {
  const compact = density === 'compact';
  const widthBySize = { sm: 300, md: compact ? 300 : 360, lg: 420 };
  const popupWidth = widthBySize[size] || widthBySize.md;

  return (
    <section
      className={classNames('bg-white text-dark overflow-hidden rounded-3', className)}
      style={{ width: `min(${popupWidth}px, calc(100vw - 32px))` }}
      aria-label={title || 'Map popup'}
    >
      <header
        className={classNames(
          'd-flex align-items-start border-bottom',
          compact ? 'gap-2 px-2 pt-2 pb-1' : 'gap-3 px-3 pt-3 pb-2',
        )}
      >
        {icon && (
          <div
            className="d-grid align-items-center justify-content-center flex-shrink-0 rounded-2"
            style={{
              width: '2.25rem',
              height: '2.25rem',
              color: '#2f5f93',
              background: 'rgba(47, 95, 147, 0.12)',
            }}
          >
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-grow-1">
          {eyebrow && (
            <div
              className="mb-1 text-secondary fw-bold text-uppercase lh-1"
              style={{ fontSize: compact ? '0.68rem' : '0.72rem' }}
            >
              {eyebrow}
            </div>
          )}
          {title && (
            <h2
              className="m-0 text-dark fw-bold text-break"
              style={{ fontSize: compact ? '0.92rem' : '1rem', lineHeight: compact ? 1.15 : 1.2 }}
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <div
              className="mt-1 text-secondary text-break"
              style={{ fontSize: compact ? '0.76rem' : '0.8rem', lineHeight: 1.25 }}
            >
              {subtitle}
            </div>
          )}
        </div>
        {onClose && (
          <CloseButton
            aria-label="Close popup"
            className="flex-shrink-0 opacity-75"
            style={{ marginTop: '-0.2rem' }}
            onClick={onClose}
          />
        )}
      </header>

      {(status || meta) && (
        <div
          className={classNames(
            'd-flex align-items-center justify-content-between bg-light border-bottom',
            compact ? 'gap-2 px-2 py-2' : 'gap-3 px-3 py-2',
          )}
        >
          {status && typeof status === 'string' ? (
            <Badge
              bg={statusVariant}
              className={classNames(
                'flex-shrink-0 rounded-pill fw-bold px-2 py-1',
                statusClassName,
              )}
            >
              {status}
            </Badge>
          ) : status ? (
            <div
              className={classNames(
                'flex-shrink-0 rounded-pill fw-bold px-2 py-1',
                statusClassName,
              )}
            >
              {status}
            </div>
          ) : null}
          {meta && (
            <div
              className="min-w-0 text-secondary text-end"
              style={{ fontSize: compact ? '0.72rem' : '0.78rem', lineHeight: 1.25 }}
            >
              {meta}
            </div>
          )}
        </div>
      )}

      <div
        className={classNames('overflow-auto', compact ? 'px-2 py-2' : 'px-3 py-2', bodyClassName)}
        style={{ maxHeight: compact ? 'min(180px, 38vh)' : 'min(420px, 62vh)' }}
      >
        {children}
      </div>

      {footer && (
        <footer
          className={classNames(
            'd-flex justify-content-end align-items-center gap-2 bg-white border-top',
            compact ? 'px-2 py-2' : 'px-3 py-2',
            footerClassName,
          )}
        >
          {typeof footer === 'string' ? (
            <Button
              variant="primary"
              size={density === 'compact' ? 'sm' : undefined}
              className="text-white"
            >
              {footer}
            </Button>
          ) : (
            footer
          )}
        </footer>
      )}
    </section>
  );
};

export default BaseMapPopup;
